import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  billingApiService,
  Invoice,
  CreateInvoiceDTO,
  UpdateInvoiceDTO,
  InvoiceFilters,
  BillingStats,
} from '../api/billing-api';

interface BillingState {
  invoices: Invoice[];
  currentInvoice: Invoice | null;
  stats: BillingStats | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: InvoiceFilters;
}

const initialState: BillingState = {
  invoices: [],
  currentInvoice: null,
  stats: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
};

// Async Thunks
export const fetchBillingStats = createAsyncThunk(
  'billing/fetchStats',
  async (year: string | undefined, { rejectWithValue }) => {
    try {
      const stats = await billingApiService.getStats(year);
      return stats;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas de facturación'
      );
    }
  }
);

export const fetchInvoices = createAsyncThunk(
  'billing/fetchInvoices',
  async (filters: InvoiceFilters = {}, { rejectWithValue }) => {
    try {
      const response = await billingApiService.getInvoices(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar facturas'
      );
    }
  }
);

export const fetchInvoice = createAsyncThunk(
  'billing/fetchInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await billingApiService.getInvoiceById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar factura'
      );
    }
  }
);

export const createInvoice = createAsyncThunk(
  'billing/createInvoice',
  async (data: CreateInvoiceDTO, { rejectWithValue }) => {
    try {
      const response = await billingApiService.createInvoice(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear factura'
      );
    }
  }
);

export const updateInvoice = createAsyncThunk(
  'billing/updateInvoice',
  async (
    { id, data }: { id: string; data: UpdateInvoiceDTO },
    { rejectWithValue }
  ) => {
    try {
      const response = await billingApiService.updateInvoice(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar factura'
      );
    }
  }
);

export const deleteInvoice = createAsyncThunk(
  'billing/deleteInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      await billingApiService.deleteInvoice(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar factura'
      );
    }
  }
);

export const markInvoiceAsPaid = createAsyncThunk(
  'billing/markAsPaid',
  async (
    { 
      id, 
      paymentData 
    }: { 
      id: string; 
      paymentData: { 
        paymentDate: string; 
        paymentMethod: string; 
        paymentReference: string;
        receipt?: File;
        notes?: string;
      } 
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await billingApiService.markAsPaid(id, paymentData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al marcar factura como pagada'
      );
    }
  }
);

// Slice
const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<InvoiceFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentInvoice: (state, action: PayloadAction<Invoice | null>) => {
      state.currentInvoice = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
  },
  extraReducers: (builder) => {
    // Fetch stats
    builder
      .addCase(fetchBillingStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBillingStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
        })
      .addCase(fetchBillingStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch invoices
    builder
      .addCase(fetchInvoices.pending, (state) => {
        if (state.invoices.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload.invoices || [];
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 10,
          total: action.payload.total || 0,
          totalPages: action.payload.totalPages || 0,
        };
        })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch invoice
    builder
      .addCase(fetchInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInvoice = action.payload;
      })
      .addCase(fetchInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create invoice
    builder
      .addCase(createInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update invoice
    builder
      .addCase(updateInvoice.pending, (state) => {
        state.error = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        const updatedInvoice = action.payload;
        const index = state.invoices.findIndex((inv) => inv.id === updatedInvoice.id);
        if (index !== -1) {
          state.invoices[index] = updatedInvoice;
        }
        if (state.currentInvoice?.id === updatedInvoice.id) {
          state.currentInvoice = updatedInvoice;
        }
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete invoice
    builder
      .addCase(deleteInvoice.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.invoices = state.invoices.filter((inv) => inv.id !== action.payload);
        state.pagination.total -= 1;
        if (state.currentInvoice?.id === action.payload) {
          state.currentInvoice = null;
        }
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Mark as paid
    builder
      .addCase(markInvoiceAsPaid.pending, (state) => {
        state.error = null;
      })
      .addCase(markInvoiceAsPaid.fulfilled, (state, action) => {
        const updatedInvoice = action.payload;
        const index = state.invoices.findIndex((inv) => inv.id === updatedInvoice.id);
        if (index !== -1) {
          state.invoices[index] = updatedInvoice;
        }
        if (state.currentInvoice?.id === updatedInvoice.id) {
          state.currentInvoice = updatedInvoice;
        }
      })
      .addCase(markInvoiceAsPaid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentInvoice,
  clearError,
  setPagination,
} = billingSlice.actions;

// Selectors
export const selectInvoices = (state: { billing: BillingState }) => state.billing.invoices;
export const selectCurrentInvoice = (state: { billing: BillingState }) => state.billing.currentInvoice;
export const selectBillingStats = (state: { billing: BillingState }) => state.billing.stats;
export const selectBillingLoading = (state: { billing: BillingState }) => state.billing.loading;
export const selectBillingError = (state: { billing: BillingState }) => state.billing.error;
export const selectBillingPagination = (state: { billing: BillingState }) => state.billing.pagination;
export const selectBillingFilters = (state: { billing: BillingState }) => state.billing.filters;

export default billingSlice.reducer;

